import { useEffect }               from 'react';
import { useRef }                  from 'react';
import getChildrenWithoutComponent from '../util/get_children_without_component';
import getComponentFromChildren    from '../util/get_component_from_children';
import React                       from 'react';
import scrollIntoView              from 'scroll-into-view-if-needed';


export default class EditableLineItem extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isEditing:    false,
      currentValue: props.value
    };

    this.handleCancel       = this.handleCancel.bind(this);
    this.handleChange       = this.handleChange.bind(this);
    this.handleSave         = this.handleSave.bind(this);
    this.handleStartEditing = this.handleStartEditing.bind(this);
    this.handleUseDefault   = this.handleUseDefault.bind(this);
  }

  handleStartEditing() {
    this.setState({ ...this.state, isEditing: true });
  }

  handleCancel() {
    this.setState({ isEditing: false, currentValue: this.props.value });
  }

  handleChange(currentValue) {
    this.setState({ ...this.state, currentValue });
  }

  async handleSave() {
    const { save }         = this.props;
    const { currentValue } = this.state;
    const isUsingDefault   = currentValue === this.props.defaultValue;

    const value = isUsingDefault ? null : currentValue;
    try {
      await save(value);
      this.handleCancel();
    } catch (error) {
      this.props.showToast(error);
    }
  }

  handleUseDefault() {
    const currentValue = this.props.defaultValue;
    this.setState({ currentValue });
  }

  render() {
    const { currentValue }       = this.state;
    const { handleCancel }       = this;
    const { handleStartEditing } = this;
    const { handleChange }       = this;
    const { handleSave }         = this;
    const { handleUseDefault }   = this;

    const { isEditing } = this.state;
    const { children }  = this.props;
    const { value }     = this.props;
    const { title }     = this.props;

    if (isEditing) {
      const props = { title, value, currentValue, handleCancel, handleChange, handleSave, handleUseDefault };
      return renderComponent(EditableLineItem.Editing, children, props);
    } else {
      const props = { title, value, handleStartEditing };
      return renderComponent(EditableLineItem.Viewing, children, props);
    }
  }
}

function renderComponent(Component, children, props) {
  const customComponent = getComponentFromChildren({
    children,
    components: [ Component ]
  });

  if (customComponent)
    return React.cloneElement(customComponent, props);
  else {
    return (
      <Component{...props}>
        {children}
      </Component>
    );
  }
}

function EditableItemViewing({ title, value, handleStartEditing, children }) {
  if (children && typeof (children) === 'function')
    return children({ title, value, handleStartEditing });

  return (
    <ListItem
      title={title}
      titleActions={<a onClick={handleStartEditing}>Edit</a>}
      content={value}
    />
  );
}

EditableLineItem.Viewing = EditableItemViewing;

function EditableItemEditing({ title, currentValue, handleCancel, children, handleChange, handleSave, handleUseDefault }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current)
      ref.current.focus();
  }, []);

  if (children && typeof (children) === 'function')
    return children({ title, currentValue, handleCancel, handleChange, handleSave, handleUseDefault });

  const buttonsComponent = getComponentFromChildren({
    children,
    components: [
      EditableLineItem.Buttons
    ]
  });

  const otherChildren = getChildrenWithoutComponent({
    children,
    component: EditableLineItem.Buttons
  });

  // If children contains other components besides the buttons, just render that.
  // Otherwise, render the buttons inside the default editing component.
  const hasOtherChildren = otherChildren?.length > 0;
  if (hasOtherChildren)
    return React.cloneElement(otherChildren, { currentValue, handleCancel, handleUseDefault, handleSave });
  else {
    return (
      <ListItem
        shouldScrollIntoView
        title={title}
        content={
          <React.Fragment>
            <textarea
              ref={ref}
              onChange={event => handleChange(event.target.value)}
              value={currentValue}
            />
            {buttonsComponent && React.cloneElement(buttonsComponent, { currentValue, handleCancel, handleUseDefault, handleSave })}
            {!buttonsComponent && (
              <Buttons
                currentValue={currentValue}
                handleCancel={handleCancel}
                handleUseDefault={handleUseDefault}
                handleSave={handleSave}
              />
            )}
          </React.Fragment>
        }
      />
    );
  }
}

EditableLineItem.Editing = EditableItemEditing;

function Buttons({ currentValue, handleSave, handleCancel, handleUseDefault, useDefaultLabel, children }) {
  const defaultLabel = useDefaultLabel || 'Use Default';

  return (
    <div className="buttons">
      <a className="link" onClick={handleCancel}>Cancel</a>
      {handleUseDefault && (<a className="link" onClick={handleUseDefault}>{defaultLabel}</a>)}
      <div className="buttons--right">
        {children && children({ currentValue, handleUseDefault, handleCancel, handleSave })}
        <a className="button button-fill active-state" onClick={handleSave}>Save</a>
      </div>
    </div>
  );
}

EditableLineItem.Buttons = Buttons;

function ListItem({ media, title, titleActions, content, after, shouldScrollIntoView }) {
  const ref = useRef(null);

  useEffect(() => {
    function scrollToElement() {
      if (ref.current && shouldScrollIntoView) {
        scrollIntoView(ref.current, {
          scrollMode: 'if-needed',
          block:      'nearest'
        });
      }
    }

    scrollToElement();
    window.addEventListener('keyboardDidShow', scrollToElement);
    return () => {
      window.removeEventListener('keyboardDidShow', scrollToElement);
    };
  }, [ shouldScrollIntoView ]);

  return (
    <li ref={ref}>
      <div className="editable-line-item item-content">
        {media && <div className="item-media">{media}</div>}
        <div className="item-inner">
          <div className="item-middle">
            {(title || titleActions) && (
              <div className="item-title-wrapper">
                {title && <div className="item-title">{title}</div>}
                {titleActions && <div className="item-title-actions">{titleActions}</div>}
              </div>
            )}
            {content && <div className="item-subtitle">{content}</div>}
          </div>
          {after && <div className="item-after">{after}</div>}
        </div>
      </div>
    </li>
  );
}

EditableLineItem.ListItem = ListItem;
